import { Search } from 'lucide-react'
import moment from 'moment'
import { parseAsString, useQueryState } from 'nuqs'
import { useEffect, useMemo, useRef, useState } from 'react'
import useSWR from 'swr'

import { CashRegisterNav } from '@/components/CashRegister/CashRegisterNav'
import { plural } from '@/components/CashRegister/registerUtils'
import { DatePaginator } from '@/components/DatePaginator/DatePaginator'
import { EetWarningBanner } from '@/components/Eet/EetWarningBanner'
import { Money, PageHeader, SegmentedTabs, StatStrip } from '@/components/ds'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Receipts, ReceiptSheet } from '@/containers/Receipts'
import { lineLabel, receiptSymbol } from '@/containers/Receipts/receiptUtils'
import { useSetPageHeader } from '@/contexts/PageHeaderContext'
import { apiFetcher } from '@/data/fetcher'
import { Collection } from '@/interfaces/Collection'
import { EetStatus, EetSubmissionCollection } from '@invo.space/shared'
import { Receipt } from '@invo.space/shared'
import { Summary } from '@invo.space/shared'
import { Printer } from '@/lib/printer/printer'

type PaymentFilter = 'all' | 'Cash' | 'Card' | 'Qr'
const PAGE = 25

export default function CashRegisterReceiptsPage() {
  const [date] = useQueryState('date', parseAsString.withDefault(moment().format('YYYY-MM-DD')))
  const [paymentParam, setPaymentParam] = useQueryState('payment')
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(0)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const printer = useRef<Printer>(new Printer())

  const payment = (paymentParam as PaymentFilter) || 'all'

  useEffect(() => {
    setPage(0)
    setSelectedId(null)
  }, [date])

  // celý den najednou, aby šlo filtrovat podle platby a hledat (den má desítky účtenek)
  const { data, isLoading, mutate } = useSWR<Collection<Receipt>>(`/api/v2/receipts?date=${date}&limit=1000`, apiFetcher)
  const { data: summary, mutate: mutateSummary } = useSWR<Summary>(`/api/v1/cash-register/daily-summary?date=${date}`, apiFetcher)
  const { data: eetSubmissions } = useSWR<EetSubmissionCollection>(`/api/v2/eet-submissions?date=${date}&limit=100`, apiFetcher)
  const { data: eetStatus } = useSWR<EetStatus>('/api/v2/eet', apiFetcher)

  const eetByReceiptId = useMemo(
    () => Object.fromEntries((eetSubmissions?.data ?? []).map((s) => [s.receiptId, s])),
    [eetSubmissions?.data],
  )
  const showEet = !!eetStatus?.enabled || (eetSubmissions?.data.length ?? 0) > 0

  useSetPageHeader({
    breadcrumbs: [
      { href: '/cash-register', children: 'Pokladna' },
      { href: '/cash-register/receipts', children: 'Účtenky' },
    ],
    newButton: 'invoice',
  })

  const receipts = useMemo(() => data?.data ?? [], [data?.data])

  const counts = useMemo(() => {
    const acc = { Cash: 0, Card: 0, Qr: 0 }
    for (const r of receipts) if (r.paymentType in acc) acc[r.paymentType as keyof typeof acc] += 1
    return acc
  }, [receipts])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return receipts
      .filter((r) => payment === 'all' || r.paymentType === payment)
      .filter((r) => !q || receiptSymbol(r).includes(q) || r.receiptLines.some((l) => lineLabel(l).toLowerCase().includes(q)))
  }, [receipts, payment, query])

  const pageReceipts = filtered.slice(page * PAGE, (page + 1) * PAGE)
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE))
  const selected = receipts.find((r) => r.id === selectedId) ?? null

  const setFilter = (value: PaymentFilter) => {
    setPaymentParam(value === 'all' ? null : value)
    setPage(0)
  }
  const toggle = (value: PaymentFilter) => setFilter(payment === value ? 'all' : value)

  const total = receipts.length
  const dayLabel = moment(date).format('D. M. YYYY')

  const byType = (type: 'Cash' | 'Card' | 'Qr') => summary?.byType?.[type]?.total

  return (
    <>
      <title>Účtenky</title>
      <div className="mx-auto w-full max-w-[1280px]">
        <EetWarningBanner />
        <CashRegisterNav className="mb-5" />

        <PageHeader
          title="Účtenky"
          description={`${dayLabel}, ${total} ${plural(total, 'účtenka', 'účtenky', 'účtenek')}`}
          actions={<DatePaginator />}
        />

        <StatStrip
          className="mb-6"
          items={[
            {
              label: 'Tržba za den',
              value: <Money amount={summary?.total.total.amount} currency={summary?.total.total.currency} size="lg" />,
              hint: summary ? (
                <span className="inline-flex flex-wrap gap-x-1">
                  bez DPH <Money amount={summary.total.totalTaxFree.amount} currency={summary.total.totalTaxFree.currency} size="sm" className="font-normal" />
                </span>
              ) : undefined,
              active: payment === 'all',
              onClick: () => setFilter('all'),
            },
            { label: 'Hotově', value: <Money amount={byType('Cash')?.amount} currency={byType('Cash')?.currency} size="lg" />, hint: `${counts.Cash} ${plural(counts.Cash, 'účtenka', 'účtenky', 'účtenek')}`, active: payment === 'Cash', onClick: () => toggle('Cash') },
            { label: 'Kartou', value: <Money amount={byType('Card')?.amount} currency={byType('Card')?.currency} size="lg" />, hint: `${counts.Card} ${plural(counts.Card, 'účtenka', 'účtenky', 'účtenek')}`, active: payment === 'Card', onClick: () => toggle('Card') },
            { label: 'QR kód', value: <Money amount={byType('Qr')?.amount} currency={byType('Qr')?.currency} size="lg" />, hint: `${counts.Qr} ${plural(counts.Qr, 'účtenka', 'účtenky', 'účtenek')}`, active: payment === 'Qr', onClick: () => toggle('Qr') },
          ]}
        />

        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <SegmentedTabs<PaymentFilter>
            value={payment}
            onChange={setFilter}
            options={[
              { value: 'all', label: 'Vše', count: total },
              { value: 'Cash', label: 'Hotově', count: counts.Cash },
              { value: 'Card', label: 'Kartou', count: counts.Card },
              { value: 'Qr', label: 'QR kód', count: counts.Qr },
            ]}
          />
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value)
                setPage(0)
              }}
              placeholder="Číslo účtenky nebo položka"
              className="pl-8"
            />
          </div>
        </div>

        <Receipts
          receipts={pageReceipts}
          isLoading={isLoading}
          eetByReceiptId={eetByReceiptId}
          showEet={showEet}
          onSelect={(r) => setSelectedId(r.id)}
          filtered={total > 0}
          footer={
            filtered.length > PAGE && (
              <div className="flex items-center justify-between border-t px-3 py-2 text-xs text-muted-foreground">
                <span className="tabular">
                  {page * PAGE + 1}-{Math.min((page + 1) * PAGE, filtered.length)} z {filtered.length}
                </span>
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                    Předchozí
                  </Button>
                  <Button variant="outline" size="sm" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)}>
                    Další
                  </Button>
                </div>
              </div>
            )
          }
        />
      </div>

      <ReceiptSheet
        receipt={selected}
        eetSubmission={selected ? eetByReceiptId[selected.id] : undefined}
        printer={printer.current}
        onOpenChange={(open) => !open && setSelectedId(null)}
        onDeleted={() => {
          mutate()
          mutateSummary()
        }}
      />
    </>
  )
}
